/**
 * Analytics wrapper — PostHog (funnel/events/replay) + Microsoft Clarity (unlimited replay backup).
 *
 * Privacy config:
 * - All <input> masked (email, password typed)
 * - Elements with class "sensitive" masked (translation content, file preview)
 * - Elements with class "ph-no-capture" blocked entirely
 * - Only commercial mode tracks (not internal VDSC)
 *
 * Docs: https://posthog.com/docs/libraries/js
 */

import posthog from 'posthog-js';
import { isCommercial } from '../config/appMode';

const POSTHOG_KEY = 'phc_A9pgeZwPagPeunQysaLjTkWhMxbLc9TH5pKhvNunRJyz';
// Reverse proxy via dichtailieu.vn/ingest → bypass ad blockers (30-40% more data).
// Proxy: functions/ingest/[[path]].ts forwards to us.i.posthog.com.
// UI host (dashboard links) stays pointing to PostHog.
const POSTHOG_HOST = 'https://dichtailieu.vn/ingest';
const POSTHOG_UI_HOST = 'https://us.posthog.com';
const CLARITY_ID = 'wg3rg5xwm8';

let initialized = false;

export function initAnalytics() {
  // Skip analytics in internal mode + localhost dev
  if (!isCommercial) return;
  if (typeof window === 'undefined') return;
  if (initialized) return;

  // Skip localhost (don't pollute production data)
  const isLocalhost = ['localhost', '127.0.0.1', '0.0.0.0'].includes(window.location.hostname);
  if (isLocalhost) {
    console.log('[Analytics] Skipped on localhost');
    return;
  }

  // PostHog init
  try {
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      ui_host: POSTHOG_UI_HOST, // dashboard links point to posthog.com UI
      autocapture: true, // auto track clicks, page changes
      capture_pageview: true,
      capture_pageleave: true,
      session_recording: {
        maskAllInputs: true, // mask all <input type=text/email/password/...>
        maskTextSelector: '.sensitive, [data-mask], .translate-output, .file-preview, .file-segment',
        blockClass: 'ph-no-capture', // elements hidden entirely
        recordCrossOriginIframes: false,
        // Filter bounce <5s (bot/accidental visits) — save quota.
        // Free tier: 5K recordings/month. Beta traffic expect ~1,500-4,000/mo (well under).
        // If approach limit → add `sample_rate: '0.5'` here.
        minimumDuration: 5000,
      },
      // Don't record passwords + sensitive query params
      sanitize_properties: (properties: any) => {
        if (properties.$current_url) {
          const url = new URL(properties.$current_url);
          // Strip ?token=xxx ?auth=xxx from URL tracking
          url.searchParams.delete('token');
          url.searchParams.delete('auth');
          url.searchParams.delete('access_token');
          properties.$current_url = url.toString();
        }
        return properties;
      },
      // Respect opt-out
      opt_out_capturing_by_default: false,
      loaded: () => {
        console.log('[Analytics] PostHog loaded');
      },
    });
  } catch (e) {
    console.error('[Analytics] PostHog init failed:', e);
  }

  // Microsoft Clarity — unlimited session replay backup, via reverse proxy to bypass ad-blockers.
  // Proxy: functions/clarity/[[path]].ts rewrites JS URLs clarity.ms → dichtailieu.vn/clarity.
  try {
    (function (c: any, l: any, a: string, r: string, i: string) {
      c[a] = c[a] || function () {
        (c[a].q = c[a].q || []).push(arguments);
      };
      const t = l.createElement(r);
      t.async = 1;
      t.src = 'https://dichtailieu.vn/clarity/tag/' + i; // proxy instead of www.clarity.ms
      const y = l.getElementsByTagName(r)[0];
      y.parentNode.insertBefore(t, y);
    })(window, document, 'clarity', 'script', CLARITY_ID);
    console.log('[Analytics] Clarity loaded (via proxy)');
  } catch (e) {
    console.error('[Analytics] Clarity init failed:', e);
  }

  initialized = true;
}

/**
 * Identify user after login — correlate session with Supabase user_id.
 */
export function identifyUser(userId: string, email?: string, createdAt?: string) {
  if (!isCommercial || typeof window === 'undefined') return;
  try {
    posthog.identify(userId, {
      email,
      signup_at: createdAt,
    });
    // Clarity identify
    if ((window as any).clarity) {
      (window as any).clarity('identify', userId, undefined, undefined, email);
    }
  } catch (e) {
    console.error('[Analytics] identify failed:', e);
  }
}

/**
 * Reset on logout — anonymize subsequent events.
 */
export function resetAnalytics() {
  if (!isCommercial || typeof window === 'undefined') return;
  try {
    posthog.reset();
  } catch (e) {
    console.error('[Analytics] reset failed:', e);
  }
}

/**
 * Track custom event.
 * Use sparingly — 10 core events only (funnel + activation + revenue).
 */
export function track(event: AnalyticsEvent, properties?: Record<string, any>) {
  if (!isCommercial || typeof window === 'undefined') return;
  try {
    posthog.capture(event, properties);
  } catch (e) {
    console.error('[Analytics] track failed:', e);
  }
}

/**
 * 10 core events for WAU funnel — keep stable schema.
 */
export type AnalyticsEvent =
  | 'cta_signup_click'          // Funnel: landing CTA clicked
  | 'auth_google_start'          // Funnel: click Google OAuth
  | 'auth_google_success'        // Funnel: OAuth completed
  | 'folder_gate_pass'           // Funnel: folder connected
  | 'file_upload'                // Funnel: file added
  | 'file_translate_start'       // Funnel: translate initiated
  | 'file_translate_done'        // Funnel: ACTIVATION moment (WAU denominator)
  | 'file_translate_retry'       // Engagement: user retry after error
  | 'credit_purchase_start'      // Revenue: click Buy button
  | 'credit_purchase_success'    // Revenue: payment confirmed
  | 'acquisition_attributed';    // Attribution: first-touch UTM persisted to profile
